import { Check, ChevronDown } from 'lucide-react';
import { Dropdown } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  LEARNING_LANGUAGE_META,
  LanguageFlag,
  useLearningLanguage,
  type LearningLanguage,
} from '@/entities/user';
import { cn } from '@/shared/lib/cn';
import { haptics } from '@/shared/lib/telegram';
import { showToast } from '@/shared/lib/toast';

/**
 * Переключатель изучаемого языка — заголовок шапки главной: «🇬🇧 ⌄» на месте,
 * где у остальных страниц стоит название. Только флаг, без названия языка —
 * по решению Дмитрия: шапка используется как компактная иконка-кнопка, а не
 * как подпись, название смотрит в списке под ней (см. `Dropdown.Item` ниже).
 * По тапу — список языков с названиями, выбор другого перечитывает всё
 * приложение под него (см. `LearningLanguageProvider`, `MainLayout`).
 *
 * Пока профиль не загрузился, места под переключатель не занимаем пустой
 * кнопкой: шапка остаётся той же высоты, просто без флага.
 */
export function LearningLanguageSwitcher() {
  const { t } = useTranslation('LearningLanguageSwitcher');
  const { language, languages, isSwitching, switchLanguage } = useLearningLanguage();

  if (!language) return <span aria-hidden="true" />;

  const current = LEARNING_LANGUAGE_META[language];

  // Выбирать не из чего (на запуск MVP сервер отдаёт только английский) —
  // просто флаг, без шеврона и меню.
  if (languages.length < 2) {
    return (
      <span
        role="img"
        aria-label={current.name}
        className="flex items-center justify-self-center px-3 py-1 text-xl font-extrabold"
      >
        <LanguageFlag language={language} />
      </span>
    );
  }

  const select = (next: LearningLanguage) => {
    if (next === language) return;
    haptics.select();
    switchLanguage(next).catch(() => showToast(t('switchFailed'), 'danger'));
  };

  return (
    <Dropdown>
      <Dropdown.Trigger
        aria-label={t('triggerLabel', { language: current.name })}
        isDisabled={isSwitching}
        className={cn(
          // Грид с зеркальной пустой колонкой слева той же ширины, что и
          // шеврон справа (как в `PageHeader` с его резервом под action) —
          // иначе флаг визуально съезжает влево от центра шапки: сам `flex
          // justify-center` центрирует всю кнопку целиком (вместе со
          // шевроном), а не то, что глаз считает заголовком.
          'group grid grid-cols-[1.25rem_auto_1.25rem] items-center gap-1.5 justify-self-center rounded-full px-3 py-1 text-xl font-extrabold text-ink outline-none',
          'data-[pressed]:bg-glass-2 data-[focus-visible]:ring-2 data-[focus-visible]:ring-signal-blue/40',
          isSwitching && 'opacity-60',
        )}
      >
        <span aria-hidden="true" />
        <LanguageFlag language={language} />
        {/* `data-[pressed]` на триггере держится, пока меню открыто (react-aria
            `MenuTrigger` пробрасывает `isPressed: state.isOpen`), не только
            на физическом нажатии — тем же атрибутом крутим стрелку. */}
        <ChevronDown
          className="size-5 shrink-0 justify-self-end text-ink-muted transition-transform duration-200 group-data-[pressed]:rotate-180"
          aria-hidden="true"
        />
      </Dropdown.Trigger>
      {/* Оформление окна — классами здесь, а не `popover.css` HeroUI: тот
          тянет утилиты анимации, которых в проекте нет (см. `index.css`).
          `z-50` — выше шапки (`z-30`) и полосы выреза (`z-40`). */}
      <Dropdown.Popover
        placement="bottom"
        offset={6}
        className="z-50 min-w-52 rounded-2xl border border-hairline bg-[var(--color-bg)] p-1 shadow-[0_12px_32px_rgba(0,0,0,0.18)] outline-none"
      >
        <Dropdown.Menu
          aria-label={t('menuLabel')}
          selectionMode="single"
          selectedKeys={[language]}
          onAction={(key) => select(key as LearningLanguage)}
        >
          {languages.map((code) => {
            const meta = LEARNING_LANGUAGE_META[code];
            return (
              <Dropdown.Item key={code} id={code} textValue={meta.name} className="flex items-center gap-3 py-2.5">
                <LanguageFlag language={code} className="text-xl" />
                <span className="flex-1 text-[15px] font-semibold text-ink">{meta.name}</span>
                {code === language && <Check className="size-4 text-ink-muted" aria-hidden="true" />}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}
